
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/lib/AuthContext";
import { 
  Swords, 
  Home, 
  Sparkles, 
  Users, 
  Trophy, 
  HelpCircle, 
  User,
  Menu,
  X,
  LogOut,
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navigationItems = [
  { title: "Home", url: createPageUrl("Home"), icon: Home },
  { title: "Features", url: createPageUrl("Features"), icon: Sparkles },
  { title: "Wiki", url: "https://knightarena.fandom.com/wiki/Knight_Arena_Wiki", icon: BookOpen, external: true },
  { title: "Community", url: createPageUrl("Community"), icon: Users },
  { title: "Rankings", url: createPageUrl("Rankings"), icon: Trophy },
  { title: "Support", url: createPageUrl("Support"), icon: HelpCircle },
];

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate(createPageUrl("Home"));
  };

  const handleLogin = () => {
    navigate(createPageUrl("Login"));
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-100">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        :root {
          --color-primary: #8B0000;
          --color-primary-light: #B22222;
          --color-accent: #ffffff;
          --color-dark: #0a0a0a;
          --color-darker: #050505;
        }
        
        .glow-text {
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
        }
        
        .glow-border {
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        
        .parallax-slow {
          transform: translateY(calc(var(--scroll) * 0.5px));
        }
      `}</style>

      {/* Navigation */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-[#0a0a0a]/95 backdrop-blur-lg border-b border-[#8B0000]/20' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to={createPageUrl("Home")} className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-[#8B0000] to-[#B22222] rounded-lg flex items-center justify-center transform group-hover:scale-110 transition-transform">
                <Swords className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white glow-text hidden sm:block">
                Knight Arena
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navigationItems.map((item) => (
                item.external ? (
                  <a
                    key={item.title}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 text-gray-300 hover:text-white transition-colors flex items-center gap-2"
                  >
                    <item.icon className="w-4 h-4" />
                    {item.title}
                  </a>
                ) : (
                  <Link
                    key={item.title}
                    to={item.url}
                    className={`px-4 py-2 transition-colors flex items-center gap-2 ${
                      location.pathname === item.url
                        ? 'text-white font-semibold'
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.title}
                  </Link>
                )
              ))}
            </div>

            {/* Auth Section */}
            <div className="hidden lg:flex items-center gap-3">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2 border-[#8B0000] text-gray-300 hover:text-white hover:border-white hover:bg-[#8B0000]/10">
                      <User className="w-4 h-4" />
                      {user.username || user.email}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#1a1a1a] border-[#8B0000]/30">
                    <DropdownMenuItem asChild>
                      <Link to={createPageUrl("Dashboard")} className="cursor-pointer text-gray-300 hover:text-white">
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-gray-300 hover:text-white">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={handleLogin}
                    className="border-[#8B0000] text-gray-300 hover:text-white hover:border-white hover:bg-[#8B0000]/10"
                  >
                    Login
                  </Button>
                  <Link to={createPageUrl("Register")}>
                    <Button className="bg-gradient-to-r from-[#8B0000] to-[#B22222] hover:from-[#B22222] hover:to-[#8B0000] text-white hover:text-white border-none">
                      Register
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-300 hover:text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#0a0a0a]/98 backdrop-blur-lg border-t border-[#8B0000]/20">
            <div className="px-4 py-6 space-y-3">
              {navigationItems.map((item) => (
                item.external ? (
                  <a
                    key={item.title}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-white hover:bg-[#8B0000]/10 rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.title}
                  </a>
                ) : (
                  <Link
                    key={item.title}
                    to={item.url}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      location.pathname === item.url
                        ? 'text-white bg-[#8B0000]/20 font-semibold'
                        : 'text-gray-300 hover:text-white hover:bg-[#8B0000]/10'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.title}
                  </Link>
                )
              ))}
              
              <div className="pt-4 border-t border-[#8B0000]/20 space-y-3">
                {user ? (
                  <>
                    <Link
                      to={createPageUrl("Dashboard")}
                      className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-white hover:bg-[#8B0000]/10 rounded-lg"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <User className="w-5 h-5" />
                      Dashboard
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-white hover:bg-[#8B0000]/10 rounded-lg"
                    >
                      <LogOut className="w-5 h-5" />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        handleLogin();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full border-[#8B0000] text-gray-300 hover:text-white hover:border-white hover:bg-[#8B0000]/10"
                    >
                      Login
                    </Button>
                    <Link to={createPageUrl("Register")} onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-gradient-to-r from-[#8B0000] to-[#B22222] hover:from-[#B22222] hover:to-[#8B0000] text-white hover:text-white border-none">
                        Register
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative z-10 bg-[#050505] border-t border-[#8B0000]/20 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Swords className="w-6 h-6 text-white" />
                <span className="text-lg font-bold text-white">Knight Arena</span>
              </div>
              <p className="text-gray-400 text-sm">
                Built by gamers, for gamers. An indie MMORPG focused on intense PvP combat in a dark medieval world.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-300 mb-4">Game</h3>
              <div className="space-y-2">
                <Link to={createPageUrl("Features")} className="block text-gray-400 hover:text-white text-sm">Features</Link>
                <Link to={createPageUrl("Rankings")} className="block text-gray-400 hover:text-white text-sm">Rankings</Link>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-300 mb-4">Community</h3>
              <div className="space-y-2">
                <Link to={createPageUrl("Community")} className="block text-gray-400 hover:text-white text-sm">Discord</Link>
                <Link to={createPageUrl("Community")} className="block text-gray-400 hover:text-white text-sm">Forum</Link>
                <Link to={createPageUrl("Support")} className="block text-gray-400 hover:text-white text-sm">Support</Link>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-300 mb-4">About</h3>
              <p className="text-gray-400 text-sm mb-2">
                Estimated Release: 2026/2027
              </p>
              <p className="text-gray-400 text-sm mb-2">
                Free to Play • No Pay to Win
              </p>
              <p className="text-gray-400 text-sm">
                100% Indie • No Shareholders
              </p>
            </div>
          </div>
          
          <div className="border-t border-[#8B0000]/20 mt-8 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; 2025 Knight Arena. Built with passion by gamers who care.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
